/**
 * Margin Threshold Constants
 *
 * Defines threshold boundaries and styling for margin percentage heat map coloring
 * in the Client Matrix dashboard.
 *
 * Thresholds (inclusive lower bound):
 * - ≥25% → success (healthy margin)
 * - ≥15% → brand (acceptable margin)
 * - ≥5%  → warning (low margin)
 * - <5%  → error (at-risk margin, includes negatives)
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Threshold level indicating margin health status
 */
export type MarginThresholdLevel = 'success' | 'brand' | 'warning' | 'error';

/**
 * Style configuration for a margin cell
 */
export interface MarginCellStyles {
  /** Tailwind background class with opacity */
  backgroundColor: string;
  /** Tailwind text color class */
  textColor: string;
  /** CSS border-radius value */
  borderRadius: string;
}

// ============================================================================
// THRESHOLD CONSTANTS
// ============================================================================

/**
 * Margin threshold boundaries (inclusive lower bound)
 * Values represent minimum percentage for each level
 */
export const MARGIN_THRESHOLDS = {
  success: 25,   // ≥25% - healthy margin
  brand: 15,     // ≥15% - acceptable margin
  warning: 5,    // ≥5%  - low margin
  // error: <5%  - at-risk (implicit, anything below warning)
} as const;

// ============================================================================
// COLOR MAPPING CONSTANTS
// ============================================================================

/**
 * Background color classes per threshold level (with opacity)
 * Uses Tailwind semantic color classes from tailwind.config.js
 */
export const MARGIN_BG_COLORS: Record<MarginThresholdLevel, string> = {
  success: 'bg-success/20',
  brand: 'bg-brand-primary/20',
  warning: 'bg-warning/20',
  error: 'bg-error/20',
};

/**
 * Text color classes per threshold level
 * Uses Tailwind semantic color classes from tailwind.config.js
 */
export const MARGIN_TEXT_COLORS: Record<MarginThresholdLevel, string> = {
  success: 'text-success',
  brand: 'text-brand-primary',
  warning: 'text-warning',
  error: 'text-error',
};

/**
 * Border radius for margin cells
 * Uses Monumenta design system rounded-monumenta (3px)
 */
export const MARGIN_CELL_BORDER_RADIUS = '3px';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determines the margin threshold level based on percentage value
 *
 * @param percent - Margin percentage (can be negative)
 * @returns Threshold level: 'success' | 'brand' | 'warning' | 'error'
 *
 * @example
 * getMarginThresholdLevel(30)   // 'success'
 * getMarginThresholdLevel(25)   // 'success' (boundary)
 * getMarginThresholdLevel(20)   // 'brand'
 * getMarginThresholdLevel(15)   // 'brand' (boundary)
 * getMarginThresholdLevel(10)   // 'warning'
 * getMarginThresholdLevel(5)    // 'warning' (boundary)
 * getMarginThresholdLevel(3)    // 'error'
 * getMarginThresholdLevel(-10)  // 'error'
 */
export function getMarginThresholdLevel(percent: number): MarginThresholdLevel {
  // Handle edge cases (null/undefined would be NaN after numeric operations)
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    return 'error';
  }

  if (percent >= MARGIN_THRESHOLDS.success) {
    return 'success';
  }
  if (percent >= MARGIN_THRESHOLDS.brand) {
    return 'brand';
  }
  if (percent >= MARGIN_THRESHOLDS.warning) {
    return 'warning';
  }
  return 'error';
}

/**
 * Returns styling configuration for a margin cell based on percentage value
 *
 * @param margemPercent - Margin percentage (can be negative)
 * @returns Style object with backgroundColor, textColor, and borderRadius
 *
 * @example
 * const styles = getMarginCellStyles(30);
 * // { backgroundColor: 'bg-success/20', textColor: 'text-success', borderRadius: '3px' }
 */
export function getMarginCellStyles(margemPercent: number): MarginCellStyles {
  const level = getMarginThresholdLevel(margemPercent);

  return {
    backgroundColor: MARGIN_BG_COLORS[level],
    textColor: MARGIN_TEXT_COLORS[level],
    borderRadius: MARGIN_CELL_BORDER_RADIUS,
  };
}
